import React, { useState, useEffect, useCallback, useId } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Plus, X, Power, Pencil, AlertTriangle, Wallet, ClipboardList, Calendar, User, Download, Send, Copy, Mail, Sparkles, Settings,
  Trash2, MessageSquare, HeartHandshake, Star, Link2, RefreshCw, Target, ShieldAlert, ClipboardCheck, Compass, Check, Smartphone,
} from 'lucide-react';
import api from '../../../utils/api';
import { SupportItemPicker } from '../../_shared/finance-ui';
import { getBranding } from '../../../utils/exportBranding';
import { generateScStatementPdf } from '../../../utils/scStatement';
import { num, isoToday } from '../ui';
import {
  Sheet, Modal, ConfirmModal, Field, Input, Textarea, Select, Switch, Checkbox, Button, Banner, Pill, Tabs, TabPanel, Empty,
  LoadingBlock, money, words, fmtDate,
} from '../kit';
import {
  FUNDING, fundingValue, LEVELS, SVC_STATUSES, COMM_CHANNEL, COMM_DIR, REPORT_TYPES, COC_ACTION_LABEL, COC_STATUS_LABEL, COC_CATEGORIES,
  fullName,
} from './shared';

// The participant file's editors and the caseload tools, on the SC kit: a side panel (Sheet) for
// every record editor and list tool, a centred Modal for the short decisions (statement, closing a
// file, drafting a note from an email), and ConfirmModal for anything that needs a yes. Fields,
// API calls and payloads are the ones these editors have always sent.

// ── Shared pieces ──────────────────────────────────────────────────────────────────────────────
// The server's own message when it sent one; otherwise a plain sentence saying what failed.
const fail = (e, fallback) => e?.response?.data?.message || fallback;
// One setter for kit inputs (an event) and kit selects and switches (a value).
const fieldSetter = (setF) => (k) => (v) => setF((p) => ({ ...p, [k]: v && v.target ? v.target.value : v }));
const opts = (pairs) => pairs.map(([value, label]) => ({ value, label }));
const fromMap = (m) => Object.entries(m).map(([value, label]) => ({ value, label }));
const staffOpts = (staff) => (staff || []).map((s) => ({ value: s.id, label: fullName(s.first_name, s.last_name) || 'Unnamed' }));

// A kit Select that keeps a stored value the list doesn't offer (an older status, a coordinator
// who has left) instead of showing it blank.
function Choice({ value, options, currentLabel, ...rest }) {
  const known = value === '' || value == null || options.some((o) => String(o.value) === String(value));
  return <Select value={value} options={known ? options : [...options, { value, label: currentLabel || words(value) }]} {...rest} />;
}

// Two fields side by side from 640px up; stacked on phones.
const Pair = ({ children, three }) => (
  <div className={clsx('grid grid-cols-1 gap-4', three ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>{children}</div>
);

// A titled group of fields inside a long editor.
function Group({ title, hint, children }) {
  return (
    <section className="space-y-4 pt-5 border-t border-slate-100 first:pt-0 first:border-t-0">
      <div>
        <h3 className="text-[13.5px] font-semibold text-slate-900">{title}</h3>
        {hint && <p className="text-[12px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

// Footer row: wraps on phones when it holds more than two buttons.
const Foot = ({ children }) => <div className="w-full flex flex-wrap items-center justify-end gap-2">{children}</div>;

// The usual footer: Cancel, then the one primary action, which submits the editor's form.
function SaveFoot({ form, busy, label, onCancel, extra }) {
  return (
    <Foot>
      {extra}
      <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      <Button type="submit" form={form} loading={busy}>{label}</Button>
    </Foot>
  );
}

// A label for the shared price-book picker, which takes no id of its own.
function PickerField({ label, hint, children }) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="space-y-1.5">
      <p id={id} className="text-[12.5px] font-semibold text-slate-700">{label}</p>
      {children}
      {hint && <p className="text-[12px] text-slate-500">{hint}</p>}
    </div>
  );
}

// ── Health condition ───────────────────────────────────────────────────────────────────────────
export function ConditionModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    condition: editing?.condition || '', diagnosis_date: (editing?.diagnosis_date || '').slice(0, 10) || '',
    managing_practitioner: editing?.managing_practitioner || '', medications: editing?.medications || '',
    status: editing?.status || 'active', notes: editing?.notes || '',
  }));
  const [busy, setBusy] = useState(false);
  const set = fieldSetter(setF);
  const save = async (e) => {
    e?.preventDefault();
    if (!f.condition.trim()) return toast.error('Enter the condition or diagnosis.');
    setBusy(true);
    try {
      if (editing) await api.put(`/support-coordination/conditions/${editing.id}`, f);
      else await api.post(`/support-coordination/${scId}/conditions`, f);
      toast.success('Condition saved'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the condition. Please try again.')); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={ClipboardCheck} title={editing ? 'Edit health condition' : 'Add a health condition'}
      sub="A condition the participant lives with, and who manages it."
      footer={<SaveFoot form="sc-condition-form" busy={busy} label="Save condition" onCancel={onClose} />}>
      <form id="sc-condition-form" onSubmit={save} noValidate className="space-y-4">
        <Field label="Condition or diagnosis" required><Input value={f.condition} onChange={set('condition')} /></Field>
        <Pair>
          <Field label="Diagnosed"><Input type="date" value={f.diagnosis_date} onChange={set('diagnosis_date')} /></Field>
          <Field label="Status">
            <Choice value={f.status} onChange={set('status')} options={opts([['active', 'Active'], ['managed', 'Managed'], ['resolved', 'Resolved']])} />
          </Field>
        </Pair>
        <Field label="Managing practitioner"><Input value={f.managing_practitioner} onChange={set('managing_practitioner')} placeholder="For example GP, psychiatrist or OT" /></Field>
        <Field label="Medications"><Textarea rows={2} value={f.medications} onChange={set('medications')} /></Field>
        <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set('notes')} /></Field>
      </form>
    </Sheet>
  );
}

// ── Risk assessment ────────────────────────────────────────────────────────────────────────────
const LIKELIHOOD = ['rare', 'unlikely', 'possible', 'likely', 'almost_certain'];
const CONSEQUENCE = ['insignificant', 'minor', 'moderate', 'major', 'severe'];
export function RiskModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    hazard: editing?.hazard || '', category: editing?.category || '',
    likelihood: editing?.likelihood || 'possible', consequence: editing?.consequence || 'moderate',
    rating: editing?.rating || 'medium', controls: editing?.controls || '', responsible: editing?.responsible || '',
    review_date: (editing?.review_date || '').slice(0, 10) || '', status: editing?.status || 'open',
  }));
  const [busy, setBusy] = useState(false);
  const set = fieldSetter(setF);
  const save = async (e) => {
    e?.preventDefault();
    if (!f.hazard.trim()) return toast.error('Describe the hazard or risk.');
    setBusy(true);
    try {
      if (editing) await api.put(`/support-coordination/risk-assessments/${editing.id}`, f);
      else await api.post(`/support-coordination/${scId}/risk-assessments`, f);
      toast.success('Risk saved'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the risk. Please try again.')); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={AlertTriangle} hue="amber" title={editing ? 'Edit risk' : 'Add a risk'}
      sub="The hazard, how likely and how serious it is, and how it is controlled."
      footer={<SaveFoot form="sc-risk-form" busy={busy} label="Save risk" onCancel={onClose} />}>
      <form id="sc-risk-form" onSubmit={save} noValidate className="space-y-4">
        <Field label="Hazard or risk" required><Input value={f.hazard} onChange={set('hazard')} /></Field>
        <Pair>
          <Field label="Category"><Input value={f.category} onChange={set('category')} placeholder="For example medical or behavioural" /></Field>
          <Field label="Rating">
            <Choice value={f.rating} onChange={set('rating')} options={opts([['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['extreme', 'Extreme']])} />
          </Field>
          <Field label="Likelihood"><Choice value={f.likelihood} onChange={set('likelihood')} options={LIKELIHOOD.map((x) => ({ value: x, label: words(x) }))} /></Field>
          <Field label="Consequence"><Choice value={f.consequence} onChange={set('consequence')} options={CONSEQUENCE.map((x) => ({ value: x, label: words(x) }))} /></Field>
        </Pair>
        <Field label="Controls and mitigation"><Textarea rows={3} value={f.controls} onChange={set('controls')} /></Field>
        <Pair>
          <Field label="Responsible"><Input value={f.responsible} onChange={set('responsible')} /></Field>
          <Field label="Review date"><Input type="date" value={f.review_date} onChange={set('review_date')} /></Field>
        </Pair>
      </form>
    </Sheet>
  );
}

// ── Crisis and emergency plan ──────────────────────────────────────────────────────────────────
const CRISIS_FIELDS = [
  ['triggers', 'Triggers', 'What can bring on a crisis'],
  ['warning_signs', 'Early warning signs', 'What to watch for'],
  ['response_steps', 'Response steps', 'What to do, in order'],
  ['de_escalation', 'What helps calm things down', 'What works for this person'],
  ['emergency_contacts', 'Emergency contacts', 'Names, roles and phone numbers'],
  ['medical_info', 'Critical medical information', 'Conditions, medications and allergies'],
];
export function CrisisModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    title: editing?.title || 'Crisis & emergency plan', triggers: editing?.triggers || '', warning_signs: editing?.warning_signs || '',
    response_steps: editing?.response_steps || '', de_escalation: editing?.de_escalation || '',
    emergency_contacts: editing?.emergency_contacts || '', medical_info: editing?.medical_info || '',
    review_date: (editing?.review_date || '').slice(0, 10) || '',
  }));
  const [busy, setBusy] = useState(false);
  const set = fieldSetter(setF);
  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.put(`/support-coordination/crisis-plans/${editing.id}`, f);
      else await api.post(`/support-coordination/${scId}/crisis-plans`, f);
      toast.success('Crisis plan saved'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the crisis plan. Please try again.')); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={ShieldAlert} hue="red" title={editing ? 'Edit crisis plan' : 'Add a crisis plan'}
      sub="What sets off a crisis, what to do, and who to call."
      footer={<SaveFoot form="sc-crisis-form" busy={busy} label="Save crisis plan" onCancel={onClose} />}>
      <form id="sc-crisis-form" onSubmit={save} noValidate className="space-y-4">
        <Field label="Plan title"><Input value={f.title} onChange={set('title')} /></Field>
        {CRISIS_FIELDS.map(([k, label, ph]) => (
          <Field key={k} label={label}><Textarea rows={2} value={f[k]} onChange={set(k)} placeholder={ph} /></Field>
        ))}
        <Field label="Review date"><Input type="date" value={f.review_date} onChange={set('review_date')} /></Field>
      </form>
    </Sheet>
  );
}

// ── Goal ───────────────────────────────────────────────────────────────────────────────────────
export function GoalModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    title: editing?.title || '', description: editing?.description || '', category: editing?.category || '',
    status: editing?.status || 'not_started', priority: editing?.priority || 'medium',
    target_date: (editing?.target_date || '').slice(0, 10) || '', progress_pct: editing?.progress_pct ?? 0,
    baseline: editing?.baseline || '', measure: editing?.measure || '', outcome: editing?.outcome || '',
  }));
  const [busy, setBusy] = useState(false);
  const set = fieldSetter(setF);
  const save = async (e) => {
    e?.preventDefault();
    if (!f.title.trim()) return toast.error('Enter the goal.');
    setBusy(true);
    try {
      if (editing) await api.put(`/support-coordination/goals/${editing.id}`, f);
      else await api.post(`/support-coordination/${scId}/goals`, f);
      toast.success('Goal saved'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the goal. Please try again.')); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={Target} title={editing ? 'Edit goal' : 'Add a goal'}
      sub="A goal from the participant’s NDIS plan, how it is measured and how it is going."
      footer={<SaveFoot form="sc-goal-form" busy={busy} label="Save goal" onCancel={onClose} />}>
      <form id="sc-goal-form" onSubmit={save} noValidate className="space-y-4">
        <Field label="Goal" required><Input value={f.title} onChange={set('title')} placeholder="For example build the confidence to travel on their own" /></Field>
        <Field label="Description"><Textarea rows={2} value={f.description} onChange={set('description')} /></Field>
        <Pair>
          <Field label="Support area"><Input value={f.category} onChange={set('category')} placeholder="For example daily living" /></Field>
          <Field label="Target date"><Input type="date" value={f.target_date} onChange={set('target_date')} /></Field>
          <Field label="Status">
            <Choice value={f.status} onChange={set('status')}
              options={opts([['not_started', 'Not started'], ['in_progress', 'In progress'], ['achieved', 'Achieved'], ['discontinued', 'Discontinued']])} />
          </Field>
          <Field label="Priority"><Choice value={f.priority} onChange={set('priority')} options={opts([['low', 'Low'], ['medium', 'Medium'], ['high', 'High']])} /></Field>
        </Pair>
        <Field label={`Progress: ${f.progress_pct}%`}>
          <input type="range" min="0" max="100" step="5" value={f.progress_pct}
            onChange={(e) => setF((p) => ({ ...p, progress_pct: Number(e.target.value) }))}
            className="w-full h-11 sm:h-8 accent-indigo-600 cursor-pointer" />
        </Field>
        <Field label="How is it measured?"><Input value={f.measure} onChange={set('measure')} placeholder="The measure or evidence of progress" /></Field>
        <Field label="Baseline"><Input value={f.baseline} onChange={set('baseline')} placeholder="Where the participant started" /></Field>
        <Field label="Outcome achieved"><Textarea rows={2} value={f.outcome} onChange={set('outcome')} placeholder="Record the outcome as the goal progresses or is achieved" /></Field>
      </form>
    </Sheet>
  );
}

// ── Closing a participant file ─────────────────────────────────────────────────────────────────
const CLOSURE_REASONS = [
  ['plan_ended', 'Plan ended'], ['goals_met', 'Goals met'], ['participant_exited', 'Participant left or opted out'],
  ['transferred', 'Moved to another provider'], ['no_longer_eligible', 'No longer eligible'], ['deceased', 'Deceased'], ['other', 'Other'],
];
export function ClosureModal({ scId, participantName, onClose, onClosed }) {
  const [f, setF] = useState({ reason: 'plan_ended', outcome: '', notes: '', closure_date: isoToday() });
  const [busy, setBusy] = useState(false);
  const set = fieldSetter(setF);
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { await api.patch(`/support-coordination/${scId}/close`, f); toast.success('Participant file closed'); onClosed(); }
    catch (err) { toast.error(fail(err, 'Could not close the file. Please try again.')); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} width="lg" title="Close participant file"
      sub={`Closing ${participantName || 'this participant'}’s file makes it inactive and records why. You can reopen it later.`}
      footer={<Foot><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form="sc-closure-form" icon={Power} loading={busy}>Close file</Button></Foot>}>
      <form id="sc-closure-form" onSubmit={submit} noValidate className="space-y-4">
        <Pair>
          <Field label="Reason"><Choice value={f.reason} onChange={set('reason')} options={opts(CLOSURE_REASONS)} /></Field>
          <Field label="Closure date"><Input type="date" value={f.closure_date} onChange={set('closure_date')} /></Field>
        </Pair>
        <Field label="Outcome"><Textarea rows={2} value={f.outcome} onChange={set('outcome')} placeholder="What was achieved, and which goals were met" /></Field>
        <Field label="Handover notes"><Textarea rows={2} value={f.notes} onChange={set('notes')} placeholder="Anything the next provider or a plan reassessment should know" /></Field>
      </form>
    </Modal>
  );
}

// ── Participant portal access: invite, resend, revoke ──────────────────────────────────────────
const PORTAL_TONE = { invited: 'amber', active: 'emerald', revoked: 'slate' };
export function PortalAccessModal({ scId, participantName, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ name: '', email: '', relationship: '' });
  const [saving, setSaving] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const set = fieldSetter(setF);
  const load = useCallback(() => { setLoading(true); api.get('/sc-portal/accounts', { params: { coordination_id: scId } }).then((r) => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, [scId]);
  useEffect(() => { load(); }, [load]);
  const invite = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) return toast.error('Enter the email address to send the invite to.');
    setSaving(true);
    try {
      const r = await api.post('/sc-portal/accounts', { coordination_id: scId, ...f });
      toast.success(r.data.emailed ? 'Invite emailed' : 'Login created. Share the link below.');
      if (!r.data.emailed && r.data.invite_url) setInviteUrl(r.data.invite_url);
      setF({ name: '', email: '', relationship: '' }); load();
    } catch (err) { toast.error(fail(err, 'Could not send the invite. Please try again.')); } finally { setSaving(false); }
  };
  const resend = async (a) => {
    try { const r = await api.post(`/sc-portal/accounts/${a.id}/resend`); toast.success(r.data.emailed ? 'Invite sent again' : 'New link made'); if (!r.data.emailed && r.data.invite_url) setInviteUrl(r.data.invite_url); }
    catch { toast.error('Could not resend the invite. Please try again.'); }
  };
  const revoke = async () => {
    const a = revoking;
    try { await api.delete(`/sc-portal/accounts/${a.id}`); load(); } catch { toast.error('Could not revoke access. Please try again.'); }
  };
  return (
    <Sheet open onClose={onClose} icon={User} title="Participant portal access" sub={participantName}
      footer={<Foot><Button variant="secondary" onClick={onClose}>Close</Button><Button type="submit" form="sc-portal-form" icon={Send} loading={saving}>Send invite</Button></Foot>}>
      <div className="space-y-6">
        <form id="sc-portal-form" onSubmit={invite} className="space-y-4">
          <Group title="Invite the participant or a nominee"
            hint="They see their budget, the support delivered and their statements, and can message you securely.">
            <Field label="Email" required><Input type="email" value={f.email} onChange={set('email')} placeholder="name@example.com" /></Field>
            <Pair>
              <Field label="Name"><Input value={f.name} onChange={set('name')} /></Field>
              <Field label="Relationship"><Input value={f.relationship} onChange={set('relationship')} placeholder="For example nominee" /></Field>
            </Pair>
            {inviteUrl && (
              <Banner tone="blue" title="Share this link with them">
                <span className="font-mono text-[12.5px] break-all">{inviteUrl}</span>
              </Banner>
            )}
          </Group>
        </form>
        <Group title="Portal logins">
          {loading ? <LoadingBlock rows={2} /> : list.length === 0 ? (
            <p className="text-[13px] text-slate-500">No portal logins yet. Send an invite above to give someone access.</p>
          ) : (
            <ul className="divide-y divide-slate-100 border-y border-slate-100">
              {list.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13.5px] font-semibold text-slate-800 truncate">{a.name || a.email}</span>
                      <Pill status={a.status} tone={PORTAL_TONE[a.status]} />
                    </div>
                    <p className="text-[12px] text-slate-500 mt-0.5 break-all">
                      {a.email}{a.relationship ? ` · ${a.relationship}` : ''}{a.last_login ? ` · last signed in ${fmtDate(a.last_login)}` : ''}
                    </p>
                  </div>
                  {a.status !== 'revoked' && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button size="sm" variant="secondary" onClick={() => resend(a)}>Resend invite</Button>
                      <Button size="sm" variant="ghost" icon={X} aria-label={`Revoke portal access for ${a.email}`} onClick={() => setRevoking(a)} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Group>
      </div>
      <ConfirmModal open={!!revoking} onClose={() => setRevoking(null)} title="Revoke portal access?" confirmLabel="Revoke access" danger onConfirm={revoke}>
        {revoking?.email} can no longer sign in to the participant portal.
      </ConfirmModal>
    </Sheet>
  );
}

// ── Feedback survey by email or SMS ────────────────────────────────────────────────────────────
export function SurveyModal({ scId, modal, onClose, onSent }) {
  const [channel, setChannel] = useState(modal.channel || 'email');
  const [to, setTo] = useState(modal.to || '');
  const [subject, setSubject] = useState('How are we doing? Your feedback');
  const [message, setMessage] = useState('We’d love your feedback on your support coordination. It only takes a moment.');
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e?.preventDefault();
    if (!to.trim()) return toast.error(channel === 'sms' ? 'Enter a phone number.' : 'Enter an email address.');
    setBusy(true);
    try {
      await api.post(`/support-coordination/${scId}/surveys`, { channel, contact_to: to, subject, message });
      toast.success('Feedback request sent'); onSent();
    } catch (err) {
      if (err.response?.status === 502) { toast.error(err.response.data?.message || 'Recorded, but not sent'); onSent(); }
      else toast.error(fail(err, 'Could not send the request. Please try again.'));
    } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={Star} title="Ask for feedback" sub="A 1 to 5 star survey by email or SMS."
      footer={<Foot><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form="sc-survey-form" icon={Send} loading={busy}>Send request</Button></Foot>}>
      <form id="sc-survey-form" onSubmit={send} noValidate className="space-y-4">
        <Pair>
          <Field label="Send by"><Choice value={channel} onChange={setChannel} options={opts([['email', 'Email'], ['sms', 'SMS']])} /></Field>
          <Field label={channel === 'sms' ? 'Phone' : 'Email'} required><Input value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </Pair>
        {channel === 'email' && <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>}
        <Field label="Message" hint="A secure 1 to 5 star feedback link is added for you. Answers appear in this participant’s Communications.">
          <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
      </form>
    </Sheet>
  );
}

// ── Statement PDF for a period ─────────────────────────────────────────────────────────────────
export function StatementModal({ scId, participantName, onClose }) {
  const thisMonth = new Date();
  const firstOfMonth = `${thisMonth.getFullYear()}-${String(thisMonth.getMonth() + 1).padStart(2, '0')}-01`;
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(isoToday());
  const [busy, setBusy] = useState(false);
  const download = async (e) => {
    e.preventDefault();
    if (from && to && from > to) return toast.error('The start date must be on or before the end date.');
    setBusy(true);
    try {
      const [r, branding] = await Promise.all([
        api.get(`/support-coordination/${scId}/statement`, { params: { from: from || undefined, to: to || undefined } }),
        getBranding(),
      ]);
      generateScStatementPdf(r.data, branding);
      toast.success('Statement downloaded'); onClose();
    } catch (err) { toast.error(fail(err, 'Could not make the statement. Please try again.')); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Statement for ${participantName || 'this participant'}`}
      sub="A PDF with your branding: the budget position and the support coordination delivered in the period."
      footer={<Foot><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form="sc-statement-form" icon={Download} loading={busy}>Download PDF</Button></Foot>}>
      <form id="sc-statement-form" onSubmit={download} className="space-y-4">
        <Pair>
          <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </Pair>
        <p className="text-[12.5px] text-slate-500">Monthly statements can also be emailed for you: turn this on in Settings, under Automation and inbox.</p>
      </form>
    </Modal>
  );
}

// ── Budget calculator: price supports from the price book ──────────────────────────────────────
const CALC_FREQ = [['weekly', 'per week', 1], ['fortnightly', 'per fortnight', 0.5], ['monthly', 'per month', 12 / 52], ['oneoff', 'one-off', null]];
export function BudgetCalculatorModal({ onClose }) {
  const blank = () => ({ id: Math.random().toString(36).slice(2), name: '', code: '', rate: '', qty: 1, freq: 'weekly' });
  const [weeks, setWeeks] = useState(52);
  const [lines, setLines] = useState([blank()]);
  const upd = (id, patch) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const lineTotal = (l) => {
    const rate = Number(l.rate) || 0; const qty = Number(l.qty) || 0;
    const freq = CALC_FREQ.find((f) => f[0] === l.freq);
    const occ = l.freq === 'oneoff' ? 1 : (Number(weeks) || 0) * (freq ? freq[2] : 1);
    return rate * qty * occ;
  };
  const grand = lines.reduce((s, l) => s + lineTotal(l), 0);
  const copy = async () => {
    const text = [
      `Support Coordination budget estimate (${weeks} weeks)`,
      ...lines.filter((l) => l.name || l.rate).map((l) => `${l.name || 'Support'}${l.code ? ` [${l.code}]` : ''}: ${money(l.rate)} × ${l.qty} ${CALC_FREQ.find((f) => f[0] === l.freq)?.[1] || ''} = ${money(lineTotal(l))}`),
      `TOTAL: ${money(grand)}`,
    ].join('\n');
    try { await navigator.clipboard.writeText(text); toast.success('Estimate copied'); } catch { toast.error('Could not copy. Select the figures and copy them yourself.'); }
  };
  return (
    <Sheet open onClose={onClose} icon={Wallet} width="lg" title="Budget calculator"
      sub="Price supports from the NDIS price book to size a support coordination budget."
      footer={(
        <Foot>
          <p className="mr-auto text-[13px] text-slate-600" aria-live="polite">Estimated budget <span className="font-semibold text-violet-800 tabular-nums">{money(grand)}</span></p>
          <Button variant="secondary" icon={Copy} onClick={copy}>Copy estimate</Button>
          <Button onClick={onClose}>Done</Button>
        </Foot>
      )}>
      <div className="space-y-4">
        <Field label="Plan length in weeks" className="max-w-[180px]">
          <Input type="number" min="0" value={weeks} onChange={(e) => setWeeks(e.target.value)} />
        </Field>
        <ul className="space-y-3">
          {lines.map((l, i) => (
            <li key={l.id} className="rounded-xl ring-1 ring-inset ring-slate-200 p-3 sm:p-4 space-y-3">
              <PickerField label={`Support ${i + 1}`}>
                <SupportItemPicker value={l.code} valueName={l.name} rate={l.rate === '' ? null : l.rate}
                  onSelect={({ code, name, rate }) => upd(l.id, { code, name, rate: rate != null ? rate : l.rate })} placeholder="Search the price book…" />
              </PickerField>
              <div className="grid grid-cols-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1.3fr)_auto] gap-3 items-end">
                <Field label="Rate ($)"><Input type="number" step="0.01" value={l.rate} onChange={(e) => upd(l.id, { rate: e.target.value })} /></Field>
                <Field label="Quantity"><Input type="number" step="0.25" value={l.qty} onChange={(e) => upd(l.id, { qty: e.target.value })} /></Field>
                <Field label="How often" className="col-span-2 sm:col-span-1">
                  <Select value={l.freq} onChange={(v) => upd(l.id, { freq: v })} options={CALC_FREQ.map(([k, lbl]) => ({ value: k, label: lbl }))} />
                </Field>
                <div className="col-span-2 sm:col-span-1 flex items-center justify-between sm:justify-end gap-2 sm:h-10">
                  <span className="text-[13.5px] font-semibold text-slate-800 tabular-nums">{money(lineTotal(l))}</span>
                  <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove support ${i + 1}`} onClick={() => setLines((ls) => ls.filter((x) => x.id !== l.id))} />
                </div>
              </div>
            </li>
          ))}
        </ul>
        <Button variant="soft" icon={Plus} onClick={() => setLines((ls) => [...ls, blank()])}>Add a support</Button>
        <p className="text-[12px] text-slate-500">An estimate only: real claims draw on the participant’s actual budgets. Weekly supports count once a week of the plan, fortnightly half as often, monthly 12 times in 52 weeks.</p>
      </div>
    </Sheet>
  );
}

// ── Supervision and CPD register ───────────────────────────────────────────────────────────────
const SUPERVISION_KIND = { supervision: 'Supervision', cpd: 'CPD', training: 'Training' };
export function SupervisionModal({ staff = [], onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState(null); // null | { editing? }
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/support-coordination/supervision', { params: { coordinator_id: filter || undefined } })
      .then((r) => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const del = async () => {
    try { await api.delete(`/support-coordination/supervision/${deleting.id}`); load(); } catch { toast.error('Could not delete the record. Please try again.'); }
  };
  const isOverdue = (d) => d && new Date(String(d).slice(0, 10)) < new Date();
  const footer = form ? (
    <Foot><Button variant="secondary" onClick={() => setForm(null)}>Back</Button><Button type="submit" form="sc-supervision-form" loading={saving}>Save record</Button></Foot>
  ) : (
    <Foot><Button variant="secondary" onClick={onClose}>Close</Button><Button icon={Plus} onClick={() => setForm({})}>New record</Button></Foot>
  );
  return (
    <Sheet open onClose={onClose} icon={ClipboardCheck} width="lg" footer={footer}
      title={form ? (form.editing ? 'Edit record' : 'New record') : 'Supervision and CPD'}
      sub="Coordinator supervision, CPD and training records.">
      {form ? (
        <SupervisionForm staff={staff} editing={form.editing} setSaving={setSaving} onSaved={() => { setForm(null); load(); }} />
      ) : (
        <div className="space-y-4">
          <Field label="Coordinator" className="sm:max-w-xs">
            <Select value={filter} onChange={setFilter} allowEmpty emptyLabel="All coordinators" options={staffOpts(staff)} />
          </Field>
          {loading ? <LoadingBlock rows={3} /> : list.length === 0 ? (
            <Empty icon={ClipboardCheck} title="No records yet">Add a supervision, CPD or training record with New record.</Empty>
          ) : (
            <ul className="divide-y divide-slate-100 border-y border-slate-100">
              {list.map((r) => {
                const overdue = isOverdue(r.next_due);
                const kind = SUPERVISION_KIND[r.kind] || words(r.kind);
                return (
                  <li key={r.id} className="flex items-start gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13.5px] font-semibold text-slate-900 break-words">{r.topic || kind || 'Record'}</p>
                      <p className="text-[12.5px] text-slate-600 mt-0.5">
                        {[r.topic ? kind : null, r.coordinator_name || 'No coordinator', r.session_date ? fmtDate(r.session_date) : null, r.hours != null && r.hours !== '' ? `${Number(r.hours).toFixed(2)} h` : null].filter(Boolean).join(' · ')}
                      </p>
                      {r.next_due && (
                        <p className={clsx('text-[12.5px] mt-1 flex flex-wrap items-center gap-2', overdue ? 'text-rose-700 font-semibold' : 'text-slate-600')}>
                          Next due {fmtDate(r.next_due)}{overdue && <Pill tone="red" label="Overdue" />}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" icon={Pencil} aria-label={`Edit ${r.topic || kind || 'record'}`} onClick={() => setForm({ editing: r })} />
                      <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Delete ${r.topic || kind || 'record'}`} onClick={() => setDeleting(r)} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <ConfirmModal open={!!deleting} onClose={() => setDeleting(null)} title="Delete this record?" confirmLabel="Delete record" danger onConfirm={del}>
        {deleting?.topic ? `“${deleting.topic}” is removed from the register.` : 'It is removed from the register.'}
      </ConfirmModal>
    </Sheet>
  );
}
function SupervisionForm({ staff, editing, setSaving, onSaved }) {
  const [f, setF] = useState(() => ({
    coordinator_id: editing?.coordinator_id || '', kind: editing?.kind || 'supervision',
    session_date: (editing?.session_date || '').slice(0, 10) || isoToday(), topic: editing?.topic || '',
    hours: editing?.hours ?? '', provider: editing?.provider || '', next_due: (editing?.next_due || '').slice(0, 10) || '', notes: editing?.notes || '',
  }));
  const set = fieldSetter(setF);
  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/support-coordination/supervision/${editing.id}`, f);
      else await api.post('/support-coordination/supervision', f);
      toast.success('Record saved'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the record. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <form id="sc-supervision-form" onSubmit={save} noValidate className="space-y-4">
      <Pair>
        <Field label="Coordinator">
          <Choice value={f.coordinator_id} onChange={set('coordinator_id')} allowEmpty emptyLabel="No coordinator" options={staffOpts(staff)}
            currentLabel={editing?.coordinator_name || 'Current coordinator'} />
        </Field>
        <Field label="Type"><Choice value={f.kind} onChange={set('kind')} options={fromMap(SUPERVISION_KIND)} /></Field>
        <Field label="Date"><Input type="date" value={f.session_date} onChange={set('session_date')} /></Field>
        <Field label="Hours"><Input type="number" step="0.5" value={f.hours} onChange={set('hours')} /></Field>
        <Field label="Topic or activity"><Input value={f.topic} onChange={set('topic')} /></Field>
        <Field label="Provider or supervisor"><Input value={f.provider} onChange={set('provider')} /></Field>
        <Field label="Next due"><Input type="date" value={f.next_due} onChange={set('next_due')} /></Field>
      </Pair>
      <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set('notes')} /></Field>
    </form>
  );
}

// ── Email or SMS several participants ──────────────────────────────────────────────────────────
export function BulkMessageModal({ scList, onClose }) {
  const active = (scList || []).filter((s) => s.is_active && s.status === 'active');
  const [channel, setChannel] = useState('email');
  const [scope, setScope] = useState('active');       // 'active' | 'selected'
  const [selected, setSelected] = useState(() => new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null); // { ids, count, what }
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const what = channel === 'sms' ? 'SMS' : 'email';
  const ask = (e) => {
    e?.preventDefault();
    if (!body.trim()) return toast.error('Write the message first.');
    const ids = scope === 'selected' ? [...selected] : undefined;
    if (scope === 'selected' && !ids.length) return toast.error('Tick at least one participant.');
    setConfirming({ ids, count: scope === 'active' ? active.length : ids.length });
  };
  const send = async () => {
    setBusy(true);
    try {
      const payload = { channel, subject, body };
      if (scope === 'active') payload.scope = 'active'; else payload.engagement_ids = confirming.ids;
      const r = await api.post('/support-coordination/bulk-communications', payload);
      toast.success(`Sent to ${r.data.sent} of ${r.data.total}${r.data.skipped?.length ? ` · ${r.data.skipped.length} skipped (no ${channel === 'sms' ? 'phone' : 'email'})` : ''}`);
      onClose();
    } catch (err) { toast.error(fail(err, 'Could not send. Please try again.')); } finally { setBusy(false); }
  };
  const count = confirming?.count || 0;
  return (
    <Sheet open onClose={onClose} icon={Send} title="Email or SMS several participants" sub="Each message is recorded in that participant’s Communications."
      footer={<Foot><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form="sc-bulk-form" icon={Send} loading={busy}>{channel === 'sms' ? 'Send SMS' : 'Send email'}</Button></Foot>}>
      <form id="sc-bulk-form" onSubmit={ask} noValidate className="space-y-4">
        <Pair>
          <Field label="Send by"><Choice value={channel} onChange={setChannel} options={opts([['email', 'Email'], ['sms', 'SMS']])} /></Field>
          <Field label="Send to">
            <Choice value={scope} onChange={setScope} options={opts([['active', `All active participants (${active.length})`], ['selected', 'Participants I choose']])} />
          </Field>
        </Pair>
        {scope === 'selected' && (
          <fieldset className="space-y-1.5">
            <legend className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Participants{selected.size > 0 ? ` (${selected.size} ticked)` : ''}</legend>
            {active.length === 0 ? <p className="text-[13px] text-slate-500">No active participants.</p> : (
              <ul className="rounded-xl ring-1 ring-inset ring-slate-200 max-h-56 overflow-y-auto divide-y divide-slate-100">
                {active.map((s) => (
                  <li key={s.id} className="px-3 py-2.5 min-h-[44px] flex items-center">
                    <Checkbox checked={selected.has(s.id)} onChange={() => toggle(s.id)} label={fullName(s.first_name, s.last_name) || 'Unnamed participant'} />
                  </li>
                ))}
              </ul>
            )}
          </fieldset>
        )}
        {channel === 'email' && <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" /></Field>}
        <Field label="Message" required hint={`Participants with no ${channel === 'sms' ? 'phone number' : 'email address'} on file are skipped.`}>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder={channel === 'sms' ? 'Keep it short: standard SMS limits apply.' : 'Type your message…'} />
        </Field>
      </form>
      <ConfirmModal open={!!confirming} onClose={() => setConfirming(null)} title={`Send ${what} to ${count}?`} confirmLabel={`Send to ${count}`} onConfirm={send}>
        This sends the {what} to {count} {scope === 'active' ? 'active' : 'selected'} participant{count === 1 ? '' : 's'} and records it in each participant’s Communications.
      </ConfirmModal>
    </Sheet>
  );
}

// ── Communication: record a call, meeting or note, or send an email or SMS ─────────────────────
export function CommModal({ scId, modal, onClose, onSaved }) {
  const sendMode = modal.send || null;           // 'email' | 'sms' when composing a send
  const editing = modal.editing || null;
  const [f, setF] = useState(() => ({
    channel: editing?.channel || sendMode || 'call',
    direction: editing?.direction || 'outgoing',
    subject: editing?.subject || '',
    body: editing?.body || '',
    contact_name: editing?.contact_name || modal.name || '',
    contact_to: editing?.contact_to || modal.to || '',
    comm_date: (editing?.comm_date || '').slice(0, 10) || isoToday(),
    follow_up_date: (editing?.follow_up_date || '').slice(0, 10) || '',
  }));
  const [busy, setBusy] = useState(false);
  const set = fieldSetter(setF);

  const save = async (e) => {
    e?.preventDefault();
    if (sendMode && !f.contact_to.trim()) return toast.error(sendMode === 'sms' ? 'Enter a phone number.' : 'Enter an email address.');
    if (sendMode && !f.body.trim()) return toast.error('Write the message first.');
    setBusy(true);
    try {
      if (sendMode) {
        await api.post(`/support-coordination/${scId}/communications/send`, { ...f, channel: sendMode });
        toast.success(sendMode === 'sms' ? 'SMS sent and recorded' : 'Email sent and recorded');
      } else if (editing) {
        await api.put(`/support-coordination/communications/${editing.id}`, f);
        toast.success('Communication updated');
      } else {
        await api.post(`/support-coordination/${scId}/communications`, f);
        toast.success('Communication recorded');
      }
      onSaved();
    } catch (err) {
      // A send that was recorded but failed returns 502 with the record: treat it as saved so the list shows it.
      if (err.response?.status === 502) { toast.error(err.response.data?.message || 'Recorded, but not sent'); onSaved(); }
      else toast.error(fail(err, sendMode ? 'Could not send. Please try again.' : 'Could not save. Please try again.'));
    } finally { setBusy(false); }
  };

  const phone = sendMode === 'sms' || f.channel === 'sms' || f.channel === 'call';
  const toLabel = phone ? 'Phone' : sendMode === 'email' || f.channel === 'email' ? 'Email' : 'Email or phone';
  const title = sendMode ? (sendMode === 'sms' ? 'Send SMS' : 'Send email') : editing ? 'Edit communication' : 'Record a communication';
  const primary = sendMode ? (sendMode === 'sms' ? 'Send SMS' : 'Send email') : editing ? 'Save communication' : 'Record communication';
  return (
    <Sheet open onClose={onClose} icon={sendMode === 'email' ? Mail : MessageSquare} title={title} sub="Recorded in this participant’s Communications."
      footer={<Foot><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form="sc-comm-form" icon={sendMode ? Send : undefined} loading={busy}>{primary}</Button></Foot>}>
      <form id="sc-comm-form" onSubmit={save} noValidate className="space-y-4">
        {!sendMode && (
          <Pair>
            <Field label="Channel"><Choice value={f.channel} onChange={set('channel')} options={fromMap(COMM_CHANNEL)} /></Field>
            <Field label="Direction"><Choice value={f.direction} onChange={set('direction')} options={fromMap(COMM_DIR)} /></Field>
          </Pair>
        )}
        <Pair>
          <Field label="Contact name"><Input value={f.contact_name} onChange={set('contact_name')} placeholder="Who" /></Field>
          <Field label={toLabel} required={!!sendMode}>
            <Input value={f.contact_to} onChange={set('contact_to')} placeholder={sendMode === 'sms' || f.channel === 'sms' ? '04xx xxx xxx' : 'name@email.com'} />
          </Field>
        </Pair>
        {!sendMode && (
          <Pair>
            <Field label="Date"><Input type="date" value={f.comm_date} onChange={set('comm_date')} /></Field>
            <Field label="Follow-up date"><Input type="date" value={f.follow_up_date} onChange={set('follow_up_date')} /></Field>
          </Pair>
        )}
        {(sendMode || f.channel !== 'call') && (
          <Field label="Subject"><Input value={f.subject} onChange={set('subject')} placeholder={sendMode === 'sms' ? 'Not sent in an SMS' : 'Subject line'} /></Field>
        )}
        <Field label={sendMode ? 'Message' : 'Summary or notes'} required={!!sendMode}>
          <Textarea value={f.body} onChange={set('body')} rows={sendMode ? 5 : 4} placeholder={sendMode ? 'Type your message…' : 'What was discussed'} />
        </Field>
        {sendMode && <Field label="Follow-up date (optional)"><Input type="date" value={f.follow_up_date} onChange={set('follow_up_date')} /></Field>}
        {sendMode === 'sms' && <p className="text-[12.5px] text-slate-500">Sent from your company’s SMS number. Standard SMS length limits apply.</p>}
        {sendMode === 'email' && <p className="text-[12.5px] text-slate-500">Sent from your company’s email address, with your branding.</p>}
      </form>
    </Sheet>
  );
}

// ── Participant (support coordination file) ────────────────────────────────────────────────────
export function SCModal({ editing, clients = [], staff = [], onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    client_id: editing?.client_id || '', coordinator_id: editing?.coordinator_id || '',
    name: editing?.name || '', funding_source: editing ? fundingValue(editing.funding_source) : 'ndia',
    level: editing?.level || 'support_coordination', disability: editing?.disability || '',
    engagement_date: editing?.engagement_date?.slice(0, 10) || '', plan_start: editing?.plan_start?.slice(0, 10) || '',
    plan_end: editing?.plan_end?.slice(0, 10) || '', ndis_number: editing?.ndis_number || '',
    status: editing?.status || 'active', notes: editing?.notes || '',
    plan_review_due: editing?.plan_review_due?.slice(0, 10) || '', progress_report_due: editing?.progress_report_due?.slice(0, 10) || '',
    next_release_date: editing?.next_release_date?.slice(0, 10) || '', next_release_info: editing?.next_release_info || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  // Create a brand-new participant here, without going to another part of Rostery.
  const [showNewClient, setShowNewClient] = useState(false);
  const [extraClients, setExtraClients] = useState([]); // created here this session
  const [nc, setNc] = useState({ first_name: '', last_name: '', ndis_number: '', date_of_birth: '', phone: '', email: '' });
  const [creating, setCreating] = useState(false);
  const setN = fieldSetter(setNc);
  const allClients = [...extraClients, ...clients.filter((c) => !extraClients.some((x) => x.id === c.id))];
  const createParticipant = async () => {
    if (!nc.first_name.trim() || !nc.last_name.trim()) return toast.error('Enter the participant’s first and last name.');
    setCreating(true);
    try {
      const r = await api.post('/support-coordination/participants', nc);
      const cl = r.data;
      setExtraClients((p) => (p.some((x) => x.id === cl.id) ? p : [cl, ...p]));
      setF((p) => ({ ...p, client_id: cl.id, ndis_number: p.ndis_number || cl.ndis_number || '' }));
      setShowNewClient(false);
      setNc({ first_name: '', last_name: '', ndis_number: '', date_of_birth: '', phone: '', email: '' });
      toast.success(cl.existing ? `${cl.first_name} ${cl.last_name} was already a participant, so they are now chosen` : 'Participant created and chosen');
    } catch (e) { toast.error(fail(e, 'Could not create the participant. Please try again.')); }
    finally { setCreating(false); }
  };
  // Coordinator app access: which coordinator app logins see this participant in the app.
  const [accounts, setAccounts] = useState([]);
  const [assigned, setAssigned] = useState([]); // account ids
  useEffect(() => {
    api.get('/sc-coordinator/accounts').then((r) => setAccounts((r.data || []).filter((a) => a.status !== 'revoked'))).catch(() => setAccounts([]));
    if (editing?.id) api.get(`/sc-coordinator/assignments/${editing.id}`).then((r) => setAssigned(r.data || [])).catch(() => setAssigned([]));
  }, [editing]);
  const toggleAssign = (id) => setAssigned((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.client_id) return toast.error('Choose the participant, or create a new one.');
    setSaving(true);
    try {
      let scId = editing?.id;
      if (editing) { await api.put(`/support-coordination/${editing.id}`, f); }
      else { const r = await api.post('/support-coordination', f); scId = r.data?.id; }
      if (scId) await api.put(`/sc-coordinator/assignments/${scId}`, { account_ids: assigned }).catch(() => {});
      toast.success(editing ? 'Participant updated' : 'Participant added');
      onSaved(editing ? undefined : scId);
    } catch (err) { toast.error(fail(err, 'Could not save the participant. Please try again.')); }
    finally { setSaving(false); }
  };

  const clientOpts = allClients.map((c) => ({ value: c.id, label: fullName(c.first_name, c.last_name) || 'Unnamed participant' }));
  const thisName = editing ? fullName(editing.client_first || editing.first_name, editing.client_last || editing.last_name) : '';
  const coordName = editing ? fullName(editing.coordinator_first, editing.coordinator_last) : '';

  return (
    <Sheet open onClose={onClose} icon={Compass} width="lg" title={editing ? 'Edit participant' : 'Add a participant'}
      sub={editing ? thisName || undefined : 'Start a support coordination file.'}
      footer={<SaveFoot form="sc-participant-form" busy={saving} label={editing ? 'Save participant' : 'Add participant'} onCancel={onClose} />}>
      <form id="sc-participant-form" onSubmit={submit} className="space-y-6">
        <div className="space-y-4">
          <Pair>
            <div className="space-y-2">
              <Field label="Participant" required hint={editing ? 'The participant can’t be changed on an existing file.' : undefined}>
                <Choice value={f.client_id} onChange={set('client_id')} disabled={!!editing} options={clientOpts}
                  placeholder="Choose a participant" currentLabel={thisName || 'This participant'} />
              </Field>
              {!editing && !showNewClient && (
                <Button size="sm" variant="soft" icon={Plus} onClick={() => setShowNewClient(true)}>Create a new participant</Button>
              )}
            </div>
            <Field label="Managing coordinator">
              <Choice value={f.coordinator_id} onChange={set('coordinator_id')} allowEmpty emptyLabel="Unassigned" options={staffOpts(staff)}
                currentLabel={coordName || 'Current coordinator'} />
            </Field>
          </Pair>
          {showNewClient && (
            <div className="rounded-xl bg-violet-50/60 ring-1 ring-inset ring-violet-100 p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-[13.5px] font-semibold text-slate-900">New participant</h3>
                  <p className="text-[12px] text-slate-600 mt-0.5">Created for Support Coordination only. They won’t appear in Plan Management.</p>
                </div>
                <Button size="sm" variant="ghost" icon={X} aria-label="Cancel the new participant" onClick={() => setShowNewClient(false)} />
              </div>
              <Pair three>
                <Field label="First name" required><Input value={nc.first_name} onChange={setN('first_name')} /></Field>
                <Field label="Last name" required><Input value={nc.last_name} onChange={setN('last_name')} /></Field>
                <Field label="NDIS number"><Input value={nc.ndis_number} onChange={setN('ndis_number')} placeholder="43xxxxxxx" inputMode="numeric" /></Field>
                <Field label="Date of birth"><Input type="date" value={nc.date_of_birth} onChange={setN('date_of_birth')} /></Field>
                <Field label="Phone"><Input value={nc.phone} onChange={setN('phone')} /></Field>
                <Field label="Email"><Input value={nc.email} onChange={setN('email')} /></Field>
              </Pair>
              <div className="flex flex-wrap justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setShowNewClient(false)}>Cancel</Button>
                <Button size="sm" icon={Check} loading={creating} onClick={createParticipant}>Create and choose</Button>
              </div>
            </div>
          )}
        </div>

        <Group title="Plan">
          <Pair>
            <Field label="Level"><Choice value={f.level} onChange={set('level')} options={fromMap(LEVELS)} /></Field>
            <Field label="Funding"><Choice value={f.funding_source} onChange={set('funding_source')} options={fromMap(FUNDING)} /></Field>
            <Field label="Plan start"><Input type="date" value={f.plan_start} onChange={set('plan_start')} /></Field>
            <Field label="Plan end"><Input type="date" value={f.plan_end} onChange={set('plan_end')} /></Field>
            <Field label="Started with us"><Input type="date" value={f.engagement_date} onChange={set('engagement_date')} /></Field>
            <Field label="NDIS number"><Input value={f.ndis_number} onChange={set('ndis_number')} placeholder="43xxxxxxx" inputMode="numeric" /></Field>
            <Field label="Primary disability or condition"><Input value={f.disability} onChange={set('disability')} /></Field>
            <Field label="Status">
              <Choice value={f.status} onChange={set('status')} options={opts([['active', 'Active'], ['on_hold', 'On hold'], ['closed', 'Closed']])} />
            </Field>
          </Pair>
        </Group>

        <Group title="Key dates" hint="These show on Overview and the calendar as they come due.">
          <Pair>
            <Field label="Plan review due"><Input type="date" value={f.plan_review_due} onChange={set('plan_review_due')} /></Field>
            <Field label="Progress report due"><Input type="date" value={f.progress_report_due} onChange={set('progress_report_due')} /></Field>
            <Field label="Next funding release"><Input type="date" value={f.next_release_date} onChange={set('next_release_date')} /></Field>
            <Field label="About the next release"><Input value={f.next_release_info} onChange={set('next_release_info')} placeholder="For example second-quarter instalment" /></Field>
          </Pair>
        </Group>

        <Group title="Other details">
          <Field label="Reference name" hint="Optional. For example Surname, First - 2024 - PLAN - 06/24 to 06/25">
            <Input value={f.name} onChange={set('name')} />
          </Field>
          <Field label="Notes"><Textarea value={f.notes} onChange={set('notes')} rows={2} /></Field>
        </Group>

        <Group title="Coordinator app access"
          hint="Tick the coordinators who should see this participant in the Coordinator app. Each coordinator sees only the participants ticked for them.">
          {accounts.length === 0 ? (
            <p className="text-[13px] text-slate-500">No Coordinator app logins yet. Create them in Settings, under Access.</p>
          ) : (
            <ul className="rounded-xl ring-1 ring-inset ring-slate-200 max-h-56 overflow-y-auto divide-y divide-slate-100">
              {accounts.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2.5 min-h-[44px]">
                  <Checkbox checked={assigned.includes(a.id)} onChange={() => toggleAssign(a.id)}
                    label={(
                      <span className="min-w-0">
                        <span className="block font-medium text-slate-800">{fullName(a.first_name, a.last_name) || a.email}</span>
                        <span className="block text-[12px] text-slate-500 break-all">{a.email}</span>
                      </span>
                    )} />
                  {a.status !== 'active' && <Pill status={a.status} tone="amber" />}
                </li>
              ))}
            </ul>
          )}
        </Group>
      </form>
    </Sheet>
  );
}

// ── Plan budget ────────────────────────────────────────────────────────────────────────────────
export function BudgetModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    budget_name: editing?.budget_name || '', support_category: editing?.support_category || '',
    budget_amount: editing?.budget_amount ?? '', released: editing?.released ?? '',
    start_date: editing?.start_date?.slice(0, 10) || '', end_date: editing?.end_date?.slice(0, 10) || '', notes: editing?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.budget_name) return toast.error('Enter the budget name.');
    setSaving(true);
    const body = { ...f, budget_amount: num(f.budget_amount), released: num(f.released) };
    try {
      if (editing) await api.put(`/support-coordination/budgets/${editing.id}`, body);
      else await api.post(`/support-coordination/${scId}/budgets`, body);
      toast.success(editing ? 'Budget updated' : 'Budget added'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the budget. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={Wallet} title={editing ? 'Edit plan budget' : 'Add a plan budget'}
      sub="A budget category in the participant’s plan."
      footer={<SaveFoot form="sc-budget-form" busy={saving} label={editing ? 'Save budget' : 'Add budget'} onCancel={onClose} />}>
      <form id="sc-budget-form" onSubmit={submit} className="space-y-4">
        <Field label="Budget name" required><Input value={f.budget_name} onChange={set('budget_name')} placeholder="Capacity Building - Support Coordination" /></Field>
        <Field label="Support category"><Input value={f.support_category} onChange={set('support_category')} placeholder="For example Capacity Building" /></Field>
        <Pair>
          <Field label="Budget amount ($)"><Input type="number" step="0.01" value={f.budget_amount} onChange={set('budget_amount')} /></Field>
          <Field label="Released ($)" hint="Amount released this period, entered by hand."><Input type="number" step="0.01" value={f.released} onChange={set('released')} /></Field>
          <Field label="Start date"><Input type="date" value={f.start_date} onChange={set('start_date')} /></Field>
          <Field label="End date"><Input type="date" value={f.end_date} onChange={set('end_date')} /></Field>
        </Pair>
        <Field label="Notes"><Textarea value={f.notes} onChange={set('notes')} rows={2} /></Field>
      </form>
    </Sheet>
  );
}

// ── Service being sourced ──────────────────────────────────────────────────────────────────────
export function ServiceModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    service_name: editing?.service_name || '', support_item_code: editing?.support_item_code || '',
    support_item_name: editing?.support_item_name || '', unit_price: editing?.unit_price ?? '', gst_code: editing?.gst_code || 'GSTFREE',
    approved_budget: editing?.approved_budget ?? '', status: editing?.status || 'sourcing',
    date_from: editing?.date_from?.slice(0, 10) || '', date_to: editing?.date_to?.slice(0, 10) || '', comments: editing?.comments || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.service_name) return toast.error('Enter the service name.');
    setSaving(true);
    const body = { ...f, approved_budget: num(f.approved_budget), unit_price: f.unit_price === '' ? null : num(f.unit_price) };
    try {
      if (editing) await api.put(`/support-coordination/services/${editing.id}`, body);
      else await api.post(`/support-coordination/${scId}/services`, body);
      toast.success(editing ? 'Service updated' : 'Service added'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the service. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={ClipboardList} title={editing ? 'Edit service' : 'Add a service'}
      sub="A support being sourced or delivered for the participant."
      footer={<SaveFoot form="sc-service-form" busy={saving} label={editing ? 'Save service' : 'Add service'} onCancel={onClose} />}>
      <form id="sc-service-form" onSubmit={submit} className="space-y-4">
        <Field label="Service name" required><Input value={f.service_name} onChange={set('service_name')} placeholder="For example physiotherapy or a SIL provider" /></Field>
        <PickerField label="NDIS support item" hint="The rate comes from the NDIS price book.">
          <SupportItemPicker category="07" value={f.support_item_code} valueName={f.support_item_name} rate={f.unit_price === '' ? null : f.unit_price}
            onSelect={({ code, name, rate }) => setF((p) => ({ ...p, support_item_code: code, support_item_name: name, unit_price: rate == null ? '' : rate }))} />
        </PickerField>
        <Pair>
          <Field label="Approved budget ($)"><Input type="number" step="0.01" value={f.approved_budget} onChange={set('approved_budget')} /></Field>
          <Field label="GST">
            <Choice value={f.gst_code} onChange={set('gst_code')} options={[{ value: 'GSTFREE', label: 'GST free' }, { value: 'P1', label: 'Includes GST', hint: 'GST code P1' }]} />
          </Field>
          <Field label="From"><Input type="date" value={f.date_from} onChange={set('date_from')} /></Field>
          <Field label="To"><Input type="date" value={f.date_to} onChange={set('date_to')} /></Field>
          <Field label="Status"><Choice value={f.status} onChange={set('status')} options={SVC_STATUSES.map((k) => ({ value: k, label: words(k) }))} /></Field>
        </Pair>
        <Field label="Comments"><Textarea value={f.comments} onChange={set('comments')} rows={2} /></Field>
      </form>
    </Sheet>
  );
}

// ── Consent ────────────────────────────────────────────────────────────────────────────────────
export function ConsentModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    consent_type: editing?.consent_type || '', granted_by: editing?.granted_by || '', relationship: editing?.relationship || '',
    method: editing?.method || 'verbal', granted_at: editing?.granted_at?.slice(0, 10) || isoToday(),
    expires_at: editing?.expires_at?.slice(0, 10) || '', status: editing?.status || 'active', notes: editing?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent_type) return toast.error('Enter what the consent is for.');
    setSaving(true);
    const body = { ...f, granted_at: f.granted_at || null, expires_at: f.expires_at || null };
    try {
      if (editing) await api.put(`/support-coordination/consents/${editing.id}`, body);
      else await api.post(`/support-coordination/${scId}/consents`, body);
      toast.success(editing ? 'Consent updated' : 'Consent recorded'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the consent. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={Check} title={editing ? 'Edit consent' : 'Record consent'}
      sub="What was agreed, by whom, when, and when it ends."
      footer={<SaveFoot form="sc-consent-form" busy={saving} label={editing ? 'Save consent' : 'Record consent'} onCancel={onClose} />}>
      <form id="sc-consent-form" onSubmit={submit} className="space-y-4">
        <Field label="Consent for" required hint="For example sharing information with providers, photos, or access to the plan.">
          <Input value={f.consent_type} onChange={set('consent_type')} />
        </Field>
        <Pair>
          <Field label="Given by"><Input value={f.granted_by} onChange={set('granted_by')} placeholder="Participant or nominee name" /></Field>
          <Field label="Relationship"><Input value={f.relationship} onChange={set('relationship')} placeholder="Self, guardian or nominee" /></Field>
          <Field label="How it was given">
            <Choice value={f.method} onChange={set('method')} options={opts([['verbal', 'Verbal'], ['written', 'Written'], ['email', 'Email'], ['form', 'Signed form']])} />
          </Field>
          <Field label="Status">
            <Choice value={f.status} onChange={set('status')} options={opts([['active', 'Active'], ['expired', 'Expired'], ['revoked', 'Revoked']])} />
          </Field>
          <Field label="Date given"><Input type="date" value={f.granted_at} onChange={set('granted_at')} /></Field>
          <Field label="Expires"><Input type="date" value={f.expires_at} onChange={set('expires_at')} /></Field>
        </Pair>
        <Field label="Notes"><Textarea value={f.notes} onChange={set('notes')} rows={2} /></Field>
      </form>
    </Sheet>
  );
}

// ── Complaint ──────────────────────────────────────────────────────────────────────────────────
export function ComplaintModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    summary: editing?.summary || '', category: editing?.category || '', severity: editing?.severity || 'medium',
    status: editing?.status || 'open', raised_by: editing?.raised_by || '', raised_at: editing?.raised_at?.slice(0, 10) || isoToday(),
    follow_up: editing?.follow_up || '', resolved_at: editing?.resolved_at?.slice(0, 10) || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.summary) return toast.error('Write a short summary of the complaint.');
    setSaving(true);
    const body = { ...f, raised_at: f.raised_at || null, resolved_at: f.resolved_at || null };
    try {
      if (editing) await api.put(`/support-coordination/complaints/${editing.id}`, body);
      else await api.post(`/support-coordination/${scId}/complaints`, body);
      toast.success(editing ? 'Complaint updated' : 'Complaint recorded'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the complaint. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={AlertTriangle} hue="amber" title={editing ? 'Edit complaint' : 'Record a complaint'}
      sub="What was raised, how serious it is, and the follow-up."
      footer={<SaveFoot form="sc-complaint-form" busy={saving} label={editing ? 'Save complaint' : 'Record complaint'} onCancel={onClose} />}>
      <form id="sc-complaint-form" onSubmit={submit} className="space-y-4">
        <Field label="Summary" required><Textarea value={f.summary} onChange={set('summary')} rows={2} /></Field>
        <Pair>
          <Field label="Category"><Input value={f.category} onChange={set('category')} placeholder="For example service quality" /></Field>
          <Field label="Severity">
            <Choice value={f.severity} onChange={set('severity')} options={opts([['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['critical', 'Critical']])} />
          </Field>
          <Field label="Status">
            <Choice value={f.status} onChange={set('status')} options={opts([['open', 'Open'], ['in_progress', 'In progress'], ['resolved', 'Resolved'], ['escalated', 'Escalated']])} />
          </Field>
          <Field label="Raised by"><Input value={f.raised_by} onChange={set('raised_by')} /></Field>
          <Field label="Date raised"><Input type="date" value={f.raised_at} onChange={set('raised_at')} /></Field>
          <Field label="Date resolved"><Input type="date" value={f.resolved_at} onChange={set('resolved_at')} /></Field>
        </Pair>
        <Field label="Follow-up"><Textarea value={f.follow_up} onChange={set('follow_up')} rows={2} /></Field>
      </form>
    </Sheet>
  );
}

// ── NDIA report register: write or AI-draft a report, then mark it submitted ───────────────────
// Editing the report text makes a new version on the server.
export function ReportRegModal({ scId, editing, participantName, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    report_type: editing?.report_type || 'progress_8week', title: editing?.title || '',
    period_from: editing?.period_from?.slice(0, 10) || '', period_to: editing?.period_to?.slice(0, 10) || '',
    due_date: editing?.due_date?.slice(0, 10) || '', status: editing?.status || 'draft',
    body: editing?.body || '', submitted_date: editing?.submitted_date?.slice(0, 10) || '', outcome: editing?.outcome || '',
  }));
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const bodyId = useId();
  const set = fieldSetter(setF);
  const aiDraft = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/support-coordination/${scId}/sc-report`, { from: f.period_from || null, to: f.period_to || null });
      if (r.data?.text) setF((p) => ({ ...p, body: p.body ? `${p.body}\n\n${r.data.text}` : r.data.text, title: p.title || `${REPORT_TYPES[p.report_type]} — ${participantName}` }));
      toast.success('AI draft added. Check it before you submit.');
    } catch (e) { toast.error(fail(e, 'The AI draft didn’t work. Please try again.')); } finally { setAiBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, period_from: f.period_from || null, period_to: f.period_to || null, due_date: f.due_date || null, submitted_date: f.submitted_date || null };
    try {
      if (editing) await api.put(`/support-coordination/reports/${editing.id}`, body);
      else await api.post(`/support-coordination/${scId}/reports`, body);
      toast.success(editing ? 'Report saved' : 'Report added'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the report. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={ClipboardCheck} width="lg" title={editing ? 'Edit report' : 'New report'}
      sub="A report to the NDIA, from draft to submitted."
      footer={<SaveFoot form="sc-report-form" busy={saving} label={editing ? 'Save report' : 'Add report'} onCancel={onClose} />}>
      <form id="sc-report-form" onSubmit={submit} className="space-y-4">
        <Pair>
          <Field label="Report type"><Choice value={f.report_type} onChange={set('report_type')} options={fromMap(REPORT_TYPES)} /></Field>
          <Field label="Status">
            <Choice value={f.status} onChange={set('status')} options={opts([['draft', 'Draft'], ['in_review', 'In review'], ['final', 'Final'], ['submitted', 'Submitted to the NDIA']])} />
          </Field>
        </Pair>
        <Field label="Title"><Input value={f.title} onChange={set('title')} placeholder="For example 8-week progress report" /></Field>
        <Pair three>
          <Field label="Period from"><Input type="date" value={f.period_from} onChange={set('period_from')} /></Field>
          <Field label="Period to"><Input type="date" value={f.period_to} onChange={set('period_to')} /></Field>
          <Field label="Due date"><Input type="date" value={f.due_date} onChange={set('due_date')} /></Field>
          {f.status === 'submitted' && <Field label="Date submitted"><Input type="date" value={f.submitted_date} onChange={set('submitted_date')} /></Field>}
        </Pair>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <label htmlFor={bodyId} className="text-[12.5px] font-semibold text-slate-700">Report</label>
            <Button size="sm" variant="soft" icon={Sparkles} loading={aiBusy} onClick={aiDraft}>Draft with AI</Button>
          </div>
          <Textarea id={bodyId} value={f.body} onChange={set('body')} rows={12} className="font-mono text-[13px]"
            placeholder="Write the report, or draft it with AI from the participant’s notes, budgets and goals for the period." />
          <p className="text-[12px] text-slate-500">The AI draft uses the notes, budgets and goals in the period above and is added below anything already written.</p>
        </div>
        <Field label="Outcome or NDIA response"><Textarea value={f.outcome} onChange={set('outcome')} rows={2} placeholder="Recorded after it is submitted" /></Field>
      </form>
    </Sheet>
  );
}

// ── Change of circumstances: a participant's situation changes and the plan action it needs ───
export function CircumstanceChangeModal({ scId, editing, onExport, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    category: editing?.category ? String(editing.category).split(',').map((s) => s.trim()).filter(Boolean) : [],
    title: editing?.title || '',
    change_date: editing?.change_date?.slice(0, 10) || isoToday(),
    identified_date: editing?.identified_date?.slice(0, 10) || isoToday(),
    reported_by: editing?.reported_by || '', reporter_role: editing?.reporter_role || 'participant',
    description: editing?.description || '', impact: editing?.impact || '',
    requested_action: editing?.requested_action || 'update_only', urgency: editing?.urgency || 'medium',
    risk_flag: editing?.risk_flag ?? false, consent_given: editing?.consent_given ?? false,
    status: editing?.status || 'draft', ndia_reference: editing?.ndia_reference || '',
    submitted_date: editing?.submitted_date?.slice(0, 10) || '', effective_date: editing?.effective_date?.slice(0, 10) || '',
    outcome: editing?.outcome || '', notes: editing?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  const chk = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggleCat = (c) => setF((p) => ({ ...p, category: p.category.includes(c) ? p.category.filter((x) => x !== c) : [...p.category, c] }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() && !f.description.trim()) { toast.error('Add a title or describe the change.'); return; }
    setSaving(true);
    const body = { ...f, change_date: f.change_date || null, identified_date: f.identified_date || null, submitted_date: f.submitted_date || null, effective_date: f.effective_date || null };
    try {
      if (editing) await api.put(`/support-coordination/circumstance-changes/${editing.id}`, body);
      else await api.post(`/support-coordination/${scId}/circumstance-changes`, body);
      toast.success(editing ? 'Change saved' : 'Change recorded'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the change. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={RefreshCw} width="lg" title={editing ? 'Edit change of circumstances' : 'Record a change of circumstances'}
      sub="When the participant’s situation changes, and what the plan needs because of it."
      footer={(
        <SaveFoot form="sc-change-form" busy={saving} label={editing ? 'Save change' : 'Record change'} onCancel={onClose}
          extra={editing && <Button variant="secondary" icon={Download} className="sm:mr-auto" onClick={() => onExport(editing.id)}>Export pack</Button>} />
      )}>
      <form id="sc-change-form" onSubmit={submit} className="space-y-6">
        <div className="space-y-4">
          <div role="group" aria-labelledby="sc-change-what" className="space-y-1.5">
            <p id="sc-change-what" className="text-[12.5px] font-semibold text-slate-700">What changed?</p>
            <div className="flex flex-wrap gap-1.5">
              {COC_CATEGORIES.map((c) => {
                const on = f.category.includes(c);
                return (
                  <button type="button" key={c} aria-pressed={on} onClick={() => toggleCat(c)}
                    className={clsx('inline-flex items-center gap-1 h-11 sm:h-8 px-3 rounded-full text-[12.5px] font-semibold ring-1 ring-inset transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                      on ? 'bg-violet-600 text-white ring-violet-600' : 'bg-white text-slate-700 ring-slate-200 hover:bg-violet-50 hover:text-violet-800 hover:ring-violet-200')}>
                    {on && <Check size={12} strokeWidth={3} aria-hidden />}{c}
                  </button>
                );
              })}
            </div>
          </div>
          <Field label="Title"><Input value={f.title} onChange={set('title')} placeholder="For example moved into supported independent living" /></Field>
          <Pair>
            <Field label="Date it changed"><Input type="date" value={f.change_date} onChange={set('change_date')} /></Field>
            <Field label="Date we found out"><Input type="date" value={f.identified_date} onChange={set('identified_date')} /></Field>
            <Field label="Reported by"><Input value={f.reported_by} onChange={set('reported_by')} placeholder="Name" /></Field>
            <Field label="Their role">
              <Choice value={f.reporter_role} onChange={set('reporter_role')}
                options={opts([['participant', 'Participant'], ['family', 'Family or carer'], ['provider', 'Provider'], ['coordinator', 'Coordinator'], ['other', 'Other']])} />
            </Field>
          </Pair>
          <Field label="What has changed"><Textarea value={f.description} onChange={set('description')} rows={3} placeholder="What has changed in the participant’s situation?" /></Field>
          <Field label="Effect on supports and the plan"><Textarea value={f.impact} onChange={set('impact')} rows={2} placeholder="How does this affect their funded supports, safety or goals?" /></Field>
        </div>

        <Group title="What happens next">
          <Pair>
            <Field label="Action needed"><Choice value={f.requested_action} onChange={set('requested_action')} options={fromMap(COC_ACTION_LABEL)} /></Field>
            <Field label="Urgency"><Choice value={f.urgency} onChange={set('urgency')} options={opts([['low', 'Low'], ['medium', 'Medium'], ['high', 'High']])} /></Field>
          </Pair>
          <div className="space-y-3">
            <Checkbox checked={!!f.risk_flag} onChange={chk('risk_flag')} label="There is a risk or safeguarding concern" />
            <Checkbox checked={!!f.consent_given} onChange={chk('consent_given')} label="The participant agrees to share this with the NDIA" />
          </div>
          <Pair>
            <Field label="Status"><Choice value={f.status} onChange={set('status')} options={fromMap(COC_STATUS_LABEL)} /></Field>
            <Field label="NDIA reference"><Input value={f.ndia_reference} onChange={set('ndia_reference')} placeholder="Once it is lodged" /></Field>
            {['submitted', 'approved', 'rejected', 'implemented'].includes(f.status) && (
              <Field label="Date submitted"><Input type="date" value={f.submitted_date} onChange={set('submitted_date')} /></Field>
            )}
            {['approved', 'implemented'].includes(f.status) && (
              <Field label="Date it takes effect"><Input type="date" value={f.effective_date} onChange={set('effective_date')} /></Field>
            )}
          </Pair>
          <Field label="Outcome or NDIA response"><Textarea value={f.outcome} onChange={set('outcome')} rows={2} placeholder="Recorded after the NDIA decides" /></Field>
          <Field label="Internal notes"><Textarea value={f.notes} onChange={set('notes')} rows={2} /></Field>
        </Group>
      </form>
    </Sheet>
  );
}

// ── Conflict of interest ───────────────────────────────────────────────────────────────────────
export function ConflictModal({ scId, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    nature: editing?.nature || '', conflict_type: editing?.conflict_type || 'referral',
    declared_by: editing?.declared_by || '', declared_date: editing?.declared_date?.slice(0, 10) || isoToday(),
    mitigation: editing?.mitigation || '', participant_informed: editing?.participant_informed ?? false,
    participant_informed_date: editing?.participant_informed_date?.slice(0, 10) || '',
    status: editing?.status || 'open', review_date: editing?.review_date?.slice(0, 10) || '', notes: editing?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.nature.trim()) return toast.error('Describe the conflict.');
    setSaving(true);
    const body = { ...f, declared_date: f.declared_date || null, participant_informed_date: f.participant_informed_date || null, review_date: f.review_date || null };
    try {
      if (editing) await api.put(`/support-coordination/conflicts/${editing.id}`, body);
      else await api.post(`/support-coordination/${scId}/conflicts`, body);
      toast.success(editing ? 'Conflict updated' : 'Conflict declared'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the conflict. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={ShieldAlert} hue="amber" title={editing ? 'Edit conflict of interest' : 'Declare a conflict of interest'}
      sub="What the conflict is, how it is managed, and whether the participant knows."
      footer={<SaveFoot form="sc-conflict-form" busy={saving} label={editing ? 'Save conflict' : 'Declare conflict'} onCancel={onClose} />}>
      <form id="sc-conflict-form" onSubmit={submit} className="space-y-4">
        <Field label="What the conflict is" required hint="For example your company also provides SIL to this participant.">
          <Textarea value={f.nature} onChange={set('nature')} rows={2} />
        </Field>
        <Pair>
          <Field label="Type">
            <Choice value={f.conflict_type} onChange={set('conflict_type')} options={opts([
              ['ownership', 'Ownership or related company'], ['referral', 'Referral to our own service'], ['familial', 'Family or personal'],
              ['financial', 'Financial'], ['other', 'Other'],
            ])} />
          </Field>
          <Field label="Status"><Choice value={f.status} onChange={set('status')} options={opts([['open', 'Open'], ['managed', 'Managed'], ['closed', 'Closed']])} /></Field>
          <Field label="Declared by"><Input value={f.declared_by} onChange={set('declared_by')} placeholder="Coordinator or staff name" /></Field>
          <Field label="Date declared"><Input type="date" value={f.declared_date} onChange={set('declared_date')} /></Field>
        </Pair>
        <Field label="How it is managed" hint="For example choice of provider offered, or oversight by a manager.">
          <Textarea value={f.mitigation} onChange={set('mitigation')} rows={2} />
        </Field>
        <Checkbox checked={!!f.participant_informed} onChange={(v) => setF((p) => ({ ...p, participant_informed: v }))} label="The participant has been told" />
        <Pair>
          {f.participant_informed && <Field label="Date told"><Input type="date" value={f.participant_informed_date} onChange={set('participant_informed_date')} /></Field>}
          <Field label="Review date"><Input type="date" value={f.review_date} onChange={set('review_date')} /></Field>
        </Pair>
        <Field label="Notes"><Textarea value={f.notes} onChange={set('notes')} rows={2} /></Field>
      </form>
    </Sheet>
  );
}

// ── Draft a note from an email: paste it, optionally summarise with AI, then Log time opens ───
export function EmailToNoteModal({ participantName, onClose, onDraft }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const useRaw = () => { if (!text.trim()) return toast.error('Paste the email first.'); onDraft(text.trim()); };
  const useAi = async () => {
    if (!text.trim()) return toast.error('Paste the email first.');
    setBusy(true);
    try {
      const r = await api.post('/support-coordination/ai/draft-note', { bullets: `Summarise this email correspondence into a case note:\n\n${text}`, participant_name: participantName });
      onDraft(r.data?.text || text.trim());
    } catch (e) { toast.error(fail(e, 'The AI summary didn’t work. Use the email as it is, or try again.')); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} width="lg" title="Draft a note from an email"
      sub="Log time opens with this as the note. Add the start and end time and the activity before you save."
      footer={(
        <Foot>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="secondary" onClick={useRaw}>Use as it is</Button>
          <Button icon={Sparkles} loading={busy} onClick={useAi}>Summarise with AI</Button>
        </Foot>
      )}>
      <Field label="Email">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder="Paste the email here…" autoFocus />
      </Field>
    </Modal>
  );
}

// ── Automation and inbox: reminders, the email inbox, the public referral form, the calendar feed
// (time rounding is set in Settings, under Time and records).
const SETTINGS_TABS = [
  { value: 'automation', label: 'Automation', icon: Sparkles },
  { value: 'email', label: 'Email inbox', icon: Mail },
  { value: 'intake', label: 'Referral form', icon: HeartHandshake },
  { value: 'calendar', label: 'Calendar feed', icon: Calendar },
];
function LinkRow({ label, url, onCopy, onRotate }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-[12.5px] font-semibold text-slate-700">{label}</label>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input id={id} readOnly value={url} onFocus={(e) => e.target.select()} className="font-mono text-[12.5px] min-w-0" />
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="secondary" icon={Link2} onClick={onCopy}>Copy link</Button>
          <Button variant="secondary" icon={RefreshCw} onClick={onRotate} title="Make a new link. The old link stops working.">New link</Button>
        </div>
      </div>
    </div>
  );
}
export function SettingsModal({ current, onClose, onSaved }) {
  const [tab, setTab] = useState('automation');
  const [reminders, setReminders] = useState(true);
  const [tasksAuto, setTasksAuto] = useState(true);
  const [statementsAuto, setStatementsAuto] = useState(false);
  const [saving, setSaving] = useState(false);
  // null while loading, true once the saved settings are in, false if they could not be read. Save
  // waits for true: saving the form's defaults over settings it never loaded would switch off the
  // inbox and reset the automation switches.
  const [settingsReady, setSettingsReady] = useState(null);
  const [imap, setImap] = useState({ ingest_enabled: false, imap_host: '', imap_port: 993, imap_secure: true, imap_user: '', imap_pass: '', imap_folder: 'INBOX', imap_pass_set: false });
  const setI = fieldSetter(setImap);
  const [intake, setIntake] = useState({ token: null, enabled: false });
  const [feed, setFeed] = useState({ token: null, enabled: false });

  useEffect(() => {
    api.get('/support-coordination/settings').then((r) => {
      setReminders(r.data?.auto_reminders !== false);
      setTasksAuto(r.data?.auto_key_date_tasks !== false);
      setStatementsAuto(r.data?.auto_statements === true);
      setImap((p) => ({
        ...p,
        ingest_enabled: r.data?.ingest_enabled === true,
        imap_host: r.data?.imap_host || '', imap_port: r.data?.imap_port || 993,
        imap_secure: r.data?.imap_secure !== false, imap_user: r.data?.imap_user || '',
        imap_folder: r.data?.imap_folder || 'INBOX', imap_pass: '', imap_pass_set: r.data?.imap_pass_set === true,
      }));
      setSettingsReady(true);
    }).catch(() => setSettingsReady(false));
    api.get('/support-coordination/intake-link').then((r) => setIntake(r.data)).catch(() => {});
    api.get('/support-coordination/calendar-feed').then((r) => setFeed(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { auto_reminders: reminders, auto_key_date_tasks: tasksAuto, auto_statements: statementsAuto,
        ingest_enabled: imap.ingest_enabled, imap_host: imap.imap_host, imap_port: Number(imap.imap_port) || 993,
        imap_secure: imap.imap_secure, imap_user: imap.imap_user, imap_folder: imap.imap_folder };
      if (imap.imap_pass) payload.imap_pass = imap.imap_pass;   // only sent when changed
      await api.put('/support-coordination/settings', payload);
      toast.success('Settings saved'); onSaved();
    } catch (e) { toast.error(fail(e, 'Could not save the settings. Please try again.')); } finally { setSaving(false); }
  };
  // The referral form and calendar feed have their own endpoints: they save as soon as they change.
  const intakeToggle = async (enabled) => { try { const r = await api.put('/support-coordination/intake-link', { enabled }); setIntake(r.data); } catch { toast.error('Could not change the referral form. Please try again.'); } };
  const intakeRotate = async () => { try { const r = await api.put('/support-coordination/intake-link', { enabled: true, rotate: true }); setIntake(r.data); toast.success('New link made'); } catch { toast.error('Could not make a new link. Please try again.'); } };
  const feedToggle = async (enabled) => { try { const r = await api.put('/support-coordination/calendar-feed', { enabled }); setFeed(r.data); } catch { toast.error('Could not change the calendar feed. Please try again.'); } };
  const feedRotate = async () => { try { const r = await api.put('/support-coordination/calendar-feed', { enabled: true, rotate: true }); setFeed(r.data); toast.success('New link made'); } catch { toast.error('Could not make a new link. Please try again.'); } };
  const copy = async (url) => { try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch { toast.error('Could not copy. Select the link and copy it yourself.'); } };
  const intakeUrl = intake.token ? `${window.location.origin}/sc-intake/${intake.token}` : null;
  const feedUrl = feed.token ? `${window.location.origin}/api/sc-public/calendar/${feed.token}.ics` : null;
  const saveReason = settingsReady === false ? 'Your saved settings couldn’t be loaded, so saving is off. Close this and open it again.'
    : settingsReady === null ? 'Loading your saved settings…' : undefined;

  return (
    <Sheet open onClose={onClose} icon={Settings} width="lg" title="Automation and inbox"
      sub="Reminders, the email inbox, the public referral form and the calendar feed."
      footer={(
        <Foot>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button onClick={save} loading={saving} disabled={settingsReady !== true} title={saveReason}>Save settings</Button>
        </Foot>
      )}>
      {settingsReady === false && (
        <div className="mb-5">
          <Banner tone="red" title="Your saved settings couldn’t be loaded">Saving is turned off so nothing is overwritten. Close this and open it again.</Banner>
        </div>
      )}
      <Tabs value={tab} onChange={setTab} tabs={SETTINGS_TABS} label="Automation settings">
        <TabPanel value="automation">
          <div className="space-y-5">
            <Switch checked={reminders} onChange={setReminders} label="Email key-date reminders"
              hint="Emails the assigned coordinator (and admins) as plan reviews, progress reports, funding releases, consent expiries and register reports come due: 30, 14 and 7 days before, and when overdue." />
            <Switch checked={tasksAuto} onChange={setTasksAuto} label="Make tasks for key dates"
              hint="Adds a task for each plan review, progress report and funding release, so the deadline is on the task list." />
            <Switch checked={statementsAuto} onChange={setStatementsAuto} label="Email monthly statements to participants"
              hint="Between the 1st and 5th of each month, emails every active participant with an email address a statement for the month before. Off unless you turn it on." />
            <p className="text-[12.5px] text-slate-500">Your admins always get a summary by email each Monday. Reminders and tasks run each morning. Save settings to keep changes on this tab.</p>
          </div>
        </TabPanel>
        <TabPanel value="email">
          <div className="space-y-5">
            <Switch checked={imap.ingest_enabled} onChange={setI('ingest_enabled')} label="Bring in emails from a mailbox"
              hint={(
                <>
                  Every 10 minutes, unread mail is recorded against the matching participant, as an incoming communication and a draft note.
                  Put <code className="text-[12px]">[SC:&lt;file id&gt;]</code> in the subject, or forward it to <code className="text-[12px]">you+&lt;file id&gt;@…</code>.
                  The file id is the last part of the participant file’s web address.
                </>
              )} />
            {imap.ingest_enabled && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_120px] gap-4">
                  <Field label="Mail server (IMAP)"><Input value={imap.imap_host} onChange={setI('imap_host')} placeholder="imap.gmail.com" /></Field>
                  <Field label="Port"><Input type="number" value={imap.imap_port} onChange={setI('imap_port')} placeholder="993" /></Field>
                </div>
                <Field label="Username"><Input value={imap.imap_user} onChange={setI('imap_user')} placeholder="mailbox@yourdomain.com" autoComplete="off" /></Field>
                <Field label="Password" hint={imap.imap_pass_set ? 'A password is saved. Leave this blank to keep it.' : 'An app password is best.'}>
                  <Input type="password" value={imap.imap_pass} onChange={setI('imap_pass')} placeholder={imap.imap_pass_set ? 'Saved password unchanged' : 'Mailbox password'} autoComplete="new-password" />
                </Field>
                <Field label="Folder"><Input value={imap.imap_folder} onChange={setI('imap_folder')} placeholder="INBOX" /></Field>
                <Switch checked={imap.imap_secure} onChange={setI('imap_secure')} label="Use a secure connection (TLS/SSL)" />
              </div>
            )}
            <p className="text-[12.5px] text-slate-500">Save settings to keep changes on this tab.</p>
          </div>
        </TabPanel>
        <TabPanel value="intake">
          <div className="space-y-5">
            <Switch checked={!!intake.enabled} onChange={intakeToggle} label={`Public referral form is ${intake.enabled ? 'on' : 'off'}`}
              hint="Share the link on your website or with referrers so they can send you a referral online. Each one arrives in Referrals as a new referral received. This switch saves straight away." />
            {intake.enabled && intakeUrl && <LinkRow label="Referral form link" url={intakeUrl} onCopy={() => copy(intakeUrl)} onRotate={intakeRotate} />}
          </div>
        </TabPanel>
        <TabPanel value="calendar">
          <div className="space-y-5">
            <Switch checked={!!feed.enabled} onChange={feedToggle} label={`Calendar feed is ${feed.enabled ? 'on' : 'off'}`}
              hint="Key dates and tasks stay in sync in your own calendar. This switch saves straight away." />
            <p className="text-[12.5px] text-slate-600">
              In Google Calendar choose <strong>Other calendars, From URL</strong>; in Outlook choose <strong>Add calendar, Subscribe from web</strong>, and paste the link.
            </p>
            {feed.enabled && feedUrl && <LinkRow label="Calendar feed link" url={feedUrl} onCopy={() => copy(feedUrl)} onRotate={feedRotate} />}
          </div>
        </TabPanel>
      </Tabs>
    </Sheet>
  );
}

// ── Coordinator app logins ─────────────────────────────────────────────────────────────────────
export function CoordLoginsModal({ onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ email: '', first_name: '', last_name: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const set = fieldSetter(setF);
  const load = () => { setLoading(true); api.get('/sc-coordinator/accounts').then((r) => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    if (!f.email || !f.password) return toast.error('Enter the email address and a starting password.');
    setSaving(true);
    try { await api.post('/sc-coordinator/accounts', f); toast.success('Coordinator login created'); setF({ email: '', first_name: '', last_name: '', password: '' }); setCreating(false); load(); }
    catch (err) { toast.error(fail(err, 'Could not create the login. Please try again.')); } finally { setSaving(false); }
  };
  const revoke = async () => {
    try { await api.delete(`/sc-coordinator/accounts/${revoking.id}`); load(); } catch { toast.error('Could not revoke the login. Please try again.'); }
  };
  const footer = creating ? (
    <Foot><Button variant="secondary" onClick={() => setCreating(false)}>Back</Button><Button type="submit" form="sc-login-form" loading={saving}>Create login</Button></Foot>
  ) : (
    <Foot><Button variant="secondary" onClick={onClose}>Close</Button><Button icon={Plus} onClick={() => setCreating(true)}>New login</Button></Foot>
  );
  return (
    <Sheet open onClose={onClose} icon={Smartphone} title={creating ? 'New coordinator login' : 'Coordinator app logins'}
      sub="Separate sign-ins for the Coordinator mobile app." footer={footer}>
      {creating ? (
        <form id="sc-login-form" onSubmit={create} className="space-y-4">
          <Pair>
            <Field label="First name"><Input value={f.first_name} onChange={set('first_name')} /></Field>
            <Field label="Last name"><Input value={f.last_name} onChange={set('last_name')} /></Field>
          </Pair>
          <Field label="Email" required><Input type="email" value={f.email} onChange={set('email')} placeholder="coordinator@example.com" autoComplete="off" /></Field>
          <Field label="Starting password" required hint="Give it to the coordinator. They use it to sign in to the Coordinator app.">
            <Input value={f.password} onChange={set('password')} placeholder="At least 8 characters" autoComplete="off" />
          </Field>
        </form>
      ) : loading ? <LoadingBlock rows={3} /> : list.length === 0 ? (
        <Empty icon={Smartphone} title="No coordinator logins yet">Create one with New login so a coordinator can use the Coordinator app.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100 border-y border-slate-100">
          {list.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-slate-800">
                  {fullName(a.first_name, a.last_name) || a.email}
                  <Pill status={a.status} tone={a.status === 'revoked' ? 'slate' : undefined} />
                </p>
                <p className="text-[12px] text-slate-500 break-all mt-0.5">{a.email}{a.last_accessed_at ? ` · last signed in ${fmtDate(a.last_accessed_at)}` : ''}</p>
              </div>
              {a.status !== 'revoked' && (
                <Button variant="ghost" size="sm" icon={X} aria-label={`Revoke the login for ${a.email}`} onClick={() => setRevoking(a)} />
              )}
            </li>
          ))}
        </ul>
      )}
      <ConfirmModal open={!!revoking} onClose={() => setRevoking(null)} title={`Revoke ${revoking?.email || 'this login'}?`} confirmLabel="Revoke login" danger onConfirm={revoke}>
        They are signed out of the Coordinator app and can’t sign in again.
      </ConfirmModal>
    </Sheet>
  );
}

// ── NDIA support coordination report, drafted by AI from goals, budgets and notes ──────────────
export function ScReportModal({ scId, onClose }) {
  const today = new Date();
  const [from, setFrom] = useState(() => `${today.getFullYear()}-01-01`);
  const [to, setTo] = useState(isoToday);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const draftId = useId();
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/support-coordination/${scId}/sc-report`, { from: from || null, to: to || null });
      setText(r.data?.text || '');
      if (!r.data?.text) toast('Nothing was drafted for this period.', { icon: 'ℹ️' });
    } catch (e) { toast.error(fail(e, 'The report draft didn’t work. Please try again.')); } finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch { toast.error('Could not copy. Select the text and copy it yourself.'); } };
  return (
    <Sheet open onClose={onClose} icon={Sparkles} width="lg" title="Support coordination report"
      sub="An AI draft for the NDIA. Check and edit it before you use it."
      footer={(
        <Foot>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {text && <Button variant="secondary" icon={Copy} onClick={copy}>Copy</Button>}
          <Button icon={Sparkles} loading={busy} onClick={generate}>{text ? 'Draft again' : 'Draft report'}</Button>
        </Foot>
      )}>
      <div className="space-y-4">
        <Pair>
          <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        </Pair>
        {busy && !text ? <LoadingBlock rows={6} /> : (
          <div className="space-y-1.5">
            <label htmlFor={draftId} className="block text-[12.5px] font-semibold text-slate-700">Draft</label>
            <Textarea id={draftId} value={text} onChange={(e) => setText(e.target.value)} rows={16} className="font-mono text-[12.5px]"
              placeholder="Choose Draft report to write it from this participant’s goals, budgets and notes. You can edit the draft here." />
          </div>
        )}
        <p className="text-[12px] text-slate-500">Drafted from the needs assessment, budgets and notes in the period. Always check and edit it before you send it to the NDIA.</p>
      </div>
    </Sheet>
  );
}

// ── Needs assessment ───────────────────────────────────────────────────────────────────────────
export function NeedsAssessmentModal({ scId, na, onClose, onSaved }) {
  const d = (v) => (v ? String(v).slice(0, 10) : '');
  const [f, setF] = useState(() => ({
    assessment_date: d(na?.assessment_date), assessor_name: na?.assessor_name || '',
    primary_disability: na?.primary_disability || '', review_date: d(na?.review_date),
    diagnosis: na?.diagnosis || '', prognosis: na?.prognosis || '', medical_history: na?.medical_history || '',
    presenting_needs: na?.presenting_needs || '', current_formal_supports: na?.current_formal_supports || '',
    current_informal_supports: na?.current_informal_supports || '', living_situation: na?.living_situation || '',
    communication_needs: na?.communication_needs || '', risk_factors: na?.risk_factors || '',
    goals: na?.goals || '', recommended_supports: na?.recommended_supports || '', outcome_measures: na?.outcome_measures || '',
    funding_requested: na?.funding_requested ?? '', funding_submitted_date: d(na?.funding_submitted_date),
    funding_approved: na?.funding_approved ?? '', funding_approved_date: d(na?.funding_approved_date),
    funding_status: na?.funding_status || 'draft', notes: na?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = fieldSetter(setF);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/support-coordination/${scId}/needs-assessment`, f);
      toast.success('Needs assessment saved'); onSaved();
    } catch (err) { toast.error(fail(err, 'Could not save the needs assessment. Please try again.')); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={ClipboardList} width="lg" title="Needs assessment"
      sub="The participant’s health, current supports, goals and the funding asked for."
      footer={<SaveFoot form="sc-needs-form" busy={saving} label="Save assessment" onCancel={onClose} />}>
      <form id="sc-needs-form" onSubmit={submit} className="space-y-6">
        <Group title="Assessment">
          <Pair>
            <Field label="Assessment date"><Input type="date" value={f.assessment_date} onChange={set('assessment_date')} /></Field>
            <Field label="Assessor"><Input value={f.assessor_name} onChange={set('assessor_name')} placeholder="Name and role" /></Field>
            <Field label="Primary disability or condition"><Input value={f.primary_disability} onChange={set('primary_disability')} /></Field>
            <Field label="Review the assessment by"><Input type="date" value={f.review_date} onChange={set('review_date')} /></Field>
          </Pair>
        </Group>
        <Group title="Health">
          <Field label="Diagnosis"><Textarea value={f.diagnosis} onChange={set('diagnosis')} rows={2} /></Field>
          <Pair>
            <Field label="Prognosis"><Textarea value={f.prognosis} onChange={set('prognosis')} rows={2} /></Field>
            <Field label="Relevant medical history"><Textarea value={f.medical_history} onChange={set('medical_history')} rows={2} /></Field>
          </Pair>
        </Group>
        <Group title="Needs and current supports">
          <Field label="Needs"><Textarea value={f.presenting_needs} onChange={set('presenting_needs')} rows={2} placeholder="What the participant needs support with" /></Field>
          <Pair>
            <Field label="Paid supports now"><Textarea value={f.current_formal_supports} onChange={set('current_formal_supports')} rows={2} placeholder="Funded or paid services" /></Field>
            <Field label="Unpaid supports now"><Textarea value={f.current_informal_supports} onChange={set('current_informal_supports')} rows={2} placeholder="Family and community" /></Field>
            <Field label="Living situation"><Input value={f.living_situation} onChange={set('living_situation')} placeholder="For example SIL, lives alone, or with family" /></Field>
            <Field label="Communication needs"><Textarea value={f.communication_needs} onChange={set('communication_needs')} rows={2} /></Field>
          </Pair>
          <Field label="Risk factors"><Textarea value={f.risk_factors} onChange={set('risk_factors')} rows={2} placeholder="Safety, behaviour or surroundings" /></Field>
        </Group>
        <Group title="Goals and recommendations">
          <Field label="Participant’s goals"><Textarea value={f.goals} onChange={set('goals')} rows={2} /></Field>
          <Field label="Recommended supports"><Textarea value={f.recommended_supports} onChange={set('recommended_supports')} rows={2} /></Field>
          <Field label="How progress is measured"><Textarea value={f.outcome_measures} onChange={set('outcome_measures')} rows={2} placeholder="For example the tools or scores used" /></Field>
        </Group>
        <Group title="Funding request">
          <Pair>
            <Field label="Funding asked for ($)"><Input type="number" step="0.01" value={f.funding_requested} onChange={set('funding_requested')} /></Field>
            <Field label="Date submitted"><Input type="date" value={f.funding_submitted_date} onChange={set('funding_submitted_date')} /></Field>
            <Field label="Funding approved ($)"><Input type="number" step="0.01" value={f.funding_approved} onChange={set('funding_approved')} /></Field>
            <Field label="Date approved"><Input type="date" value={f.funding_approved_date} onChange={set('funding_approved_date')} /></Field>
            <Field label="Status">
              <Choice value={f.funding_status} onChange={set('funding_status')} options={opts([['draft', 'Draft'], ['submitted', 'Submitted'], ['approved', 'Approved'], ['declined', 'Declined']])} />
            </Field>
          </Pair>
          <Field label="Notes"><Textarea value={f.notes} onChange={set('notes')} rows={2} /></Field>
        </Group>
      </form>
    </Sheet>
  );
}
